import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Chip,
  CircularProgress,
  InputLabel,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import LockIcon from '@mui/icons-material/Lock'
import useMultiSampleOptions from 'src/hooks/useMultiSampleOptions'

const predefinedRelations = [
  'Proband',
  'Mother',
  'Father',
  'Sibling',
  'Grandparent',
  'Aunt',
  'Uncle',
  'Cousin',
  'Son',
  'Daughter',
]

const TAG_COLOR_MAP = {
  Germline: 'primary',
  Somatic: 'secondary',
  VCF: 'default',
  FASTQ: 'default',
  WGS: 'success',
  WES: 'info',
  AI: 'warning',
}

// Autocomplete for finding analysis samples
function SampleAutocomplete({ fileId, selectedOption, onChange, disabledFileIds, error, helperText }) {
  const { options, isSearching, setSearchTerm } = useMultiSampleOptions()
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })

  useEffect(() => {
    if (fileId && !selectedOption) {
      const found = options.find((o) => o.id === fileId)
      if (found) onChangeRef.current(found)
    }
  }, [fileId, selectedOption, options])

  const displayOptions = useMemo(() => {
    if (!selectedOption || options.some((o) => o.id === selectedOption.id)) return options
    return [selectedOption, ...options]
  }, [options, selectedOption])

  return (
    <Autocomplete
      options={displayOptions}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      value={selectedOption}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, value, reason) => {
        if (reason === 'input') setSearchTerm(value)
      }}
      onClose={() => setSearchTerm('')}
      loading={isSearching}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Sample"
          placeholder="Search samples..."
          error={error}
          helperText={helperText}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isSearching ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            opacity: !option.completed || disabledFileIds.includes(option.id) ? 0.5 : 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start !important',
            gap: 0.5,
            py: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {option.label}
            </Typography>
            {!option.completed && (
              <Tooltip title="Analysis not completed" arrow>
                <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </Tooltip>
            )}
          </Box>
          {option.tags?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {option.tags
                .filter((t) => t !== 'AI')
                .map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    color={TAG_COLOR_MAP[tag] || 'default'}
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                ))}
            </Box>
          )}
        </Box>
      )}
      getOptionDisabled={(option) => !option.completed || disabledFileIds.includes(option.id)}
      noOptionsText={isSearching ? 'Searching...' : 'No samples found'}
    />
  )
}

/**
 * StepMultisampleSelect — sample selection step for Multisample analysis.
 * Props:
 *   - samples: array of { fileId, relation, include, gt, selectedOption }
 *   - onChange: (samples) => void
 */
const StepMultisampleSelect = ({ samples, onChange }) => {
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [touchedFields, setTouchedFields] = useState({})

  const disabledFileIds = useMemo(() => samples.map((s) => s.fileId).filter(Boolean), [samples])

  const handleAdd = () => {
    onChange([...samples, { fileId: '', relation: '', include: true, gt: 'any', selectedOption: null }])
  }

  const handleDelete = (index) => {
    onChange(samples.filter((_, i) => i !== index))
  }

  const handleChange = (index, field, value) => {
    let updated
    if (field === 'include' && value === false) {
      // When excluding, force genotype to 'any'
      updated = samples.map((s, i) => (i === index ? { ...s, include: false, gt: 'any' } : s))
    } else {
      updated = samples.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    }
    onChange(updated)
    setTouchedFields((prev) => ({ ...prev, [index]: { ...prev[index], [field]: true } }))
  }

  const handleSampleSelect = (index, option) => {
    const updated = samples.map((s, i) =>
      i === index ? { ...s, fileId: option?.id || '', selectedOption: option } : s,
    )
    onChange(updated)
    setTouchedFields((prev) => ({ ...prev, [index]: { ...prev[index], fileId: true } }))
  }

  const showError = (index, field) => {
    return (attemptedSubmit || touchedFields[index]?.[field]) && !samples[index][field]
  }

  return (
    // Sample cards sit two per row from md up (a single card stays centred), so the
    // step uses the wizard's full width.
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom color="primary" sx={{ textAlign: 'center', mb: 2 }}>
        Select Samples for Multisample Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
        Add samples to include or exclude from variant intersection. Each sample needs a relation tag and genotype
        filter.
      </Typography>

      <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
          {samples.map((sample, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{ p: 1.5, width: { xs: '100%', md: 'calc(50% - 8px)' }, maxWidth: 640, minWidth: 0 }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Sample {index + 1}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ToggleButtonGroup
                      value={sample.include}
                      exclusive
                      onChange={(_, value) => {
                        if (value != null) handleChange(index, 'include', value)
                      }}
                      size="small"
                    >
                      <ToggleButton value={true} color="primary">
                        <Tooltip title="Include variants" arrow>
                          <CheckCircleIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value={false} color="secondary">
                        <Tooltip title="Exclude variants" arrow>
                          <CancelIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {samples.length > 1 && (
                      <IconButton size="small" onClick={() => handleDelete(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Stack>

                <FormControl fullWidth size="small">
                  <SampleAutocomplete
                    fileId={sample.fileId}
                    selectedOption={sample.selectedOption || null}
                    onChange={(option) => handleSampleSelect(index, option)}
                    disabledFileIds={disabledFileIds}
                    error={showError(index, 'fileId')}
                    helperText={showError(index, 'fileId') ? 'Sample is required' : ''}
                  />
                </FormControl>

                <Stack direction="row" spacing={1}>
                  <Autocomplete
                    freeSolo
                    options={predefinedRelations}
                    value={sample.relation}
                    onChange={(_, value) => handleChange(index, 'relation', value || '')}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') handleChange(index, 'relation', value)
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Relation"
                        size="small"
                        error={showError(index, 'relation')}
                        helperText={showError(index, 'relation') ? 'Relation is required' : ''}
                      />
                    )}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Genotype</InputLabel>
                    <Select
                      value={sample.include ? sample.gt : 'any'}
                      label="Genotype"
                      onChange={(e) => handleChange(index, 'gt', e.target.value)}
                      disabled={!sample.include}
                    >
                      <MenuItem value="any">Any</MenuItem>
                      <MenuItem value="heterozygote">Heterozygote</MenuItem>
                      <MenuItem value="homozygote">Homozygote</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Box>

        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd} sx={{ alignSelf: 'center' }}>
          Add Sample
        </Button>
      </Stack>
    </Box>
  )
}

export default StepMultisampleSelect
